import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  Dropdown,
  Input,
  Modal,
  Form,
  Select,
  Checkbox,
  Upload,
  message,
  Spin,
  Tooltip,
  Tag,
} from "antd";

import { UploadOutlined, DeleteOutlined, RobotOutlined, PlusOutlined, EditOutlined, PoweroffOutlined, MoreOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { generateProductImages } from "../../services/gemini";

import {
  getProductsApi,
  createProductApi,
  updateProductApi,
  updateProductStatusApi,
  deleteProductApi,
  getCategoriesApi,
  getHsnCodesApi,
  uploadImageApi,
  deleteImageApi,
} from "../../services/api";
import AppInput from "../../common/FormInputs/AppInput";
import AppSelect from "../../common/FormInputs/AppSelect";
import AppCheckbox from "../../common/FormInputs/AppCheckbox";
import AppTable from "../../common/AppTable";

const { Search } = Input;

export default function Products() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [hsnCodes, setHsnCodes] = useState([]);
  const [cat, setCat] = useState("All");
  const [search, setSearch] = useState(""); // committed search term (server-side)
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [open, setOpen] = useState(false);
  const [hasVariants, setHasVariants] = useState(false);
  const [form] = Form.useForm();
  const [mode, setMode] = useState("add"); // add | edit
  const [editingProduct, setEditingProduct] = useState(null);

  const [variants, setVariants] = useState([
    { size: "", price: "", offerPrice: "", color: "", sku: "", material: "" },
  ]);
  const [specs, setSpecs] = useState([{ label: "", value: "" }]);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [uploadingIdx,   setUploadingIdx]   = useState(null);
  const [aiLoading,      setAiLoading]      = useState(false);

const fetchProducts = async (current = pagination.current, pageSize = pagination.pageSize) => {
  setLoading(true);
  try {
    const res = await getProductsApi({
      page: current - 1,
      size: pageSize,
      search: search || undefined,
    });
    const data = res.data.data || {};
    setProducts(data.items || []);
    setPagination((p) => ({ ...p, current, pageSize, total: data.total || 0 }));
  } catch (err) {
    console.log(err);
  } finally {
    setLoading(false);
  }
};

  const fetchCategories = async () => {
    try {
      const res = await getCategoriesApi();
      setCategories(res.data.data);
    } catch (err) {
      console.log(err);
    }
  };

  const fetchHsnCodes = async () => {
    try {
      const res = await getHsnCodesApi();
      setHsnCodes(res.data.data || []);
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    fetchCategories();
    fetchHsnCodes();
  }, []);

  useEffect(() => {
    fetchProducts(pagination.current, pagination.pageSize);
  }, [pagination.current, pagination.pageSize, search]);

  const handleSearch = (value) => {
    setSearch(value);
    setPagination((p) => ({ ...p, current: 1 }));
  };

  // ================= TOGGLE ACTIVE =================
  const handleToggleActive = async (record) => {
    try {
      await updateProductStatusApi(record.id, !record.active);
      message.success(record.active ? "Product deactivated" : "Product activated");
      fetchProducts();
    } catch (err) {
      message.error(err?.response?.data?.data || "Failed to update status");
    }
  };

  // ================= DELETE =================
  const handleDelete = async (id) => {
    try {
      await deleteProductApi(id);
      fetchProducts();
    } catch (err) {
      console.log(err);
    }
  };
  // ── S3 upload helper ──────────────────────────────────────────
  const runImageUpload = async (file) => {
    const catId   = form.getFieldValue("category");
    const catName = categories.find((c) => c.id === catId)?.name || "general";
    const folder  = `products/${catName.toLowerCase().replace(/\s+/g, "-")}`;

    setUploadingIdx(Date.now());
    try {
      const res = await uploadImageApi(file, folder);
      const url = res?.data?.data;
      if (url) setUploadedImages((prev) => [...prev, url]);
    } catch (e) {
      console.error("Image upload error:", e);
      message.error("Image upload failed");
    } finally {
      setUploadingIdx(null);
    }
  };

  const handleImageUpload = (file) => {
    runImageUpload(file);
    return false;
  };

  const handleRemoveImage = async (url) => {
    try { await deleteImageApi(url); } catch { /* ignore */ }
    setUploadedImages((prev) => prev.filter((u) => u !== url));
  };

  const runAiUpload = async (file) => {
    setAiLoading(true);
    setUploadingIdx(Date.now());
    try {
      const urls = await generateProductImages(file);
      if (urls && urls.length > 0) {
        setUploadedImages((prev) => [...prev, ...urls]);
        message.success(`${urls.length} AI product image${urls.length > 1 ? "s" : ""} added!`);
      } else {
        message.warning("AI returned no images.");
      }
    } catch (e) {
      console.error("AI image generation error:", e);
      const msg = e?.response?.data?.data || e?.message || "Unknown error";
      message.error("AI generation failed: " + msg);
    } finally {
      setUploadingIdx(null);
      setAiLoading(false);
    }
  };

  const handleAiUpload = (file) => {
    runAiUpload(file);
    return false;
  };

  const handleEdit = (product) => {
    setMode("edit");
    setEditingProduct(product);
    setUploadedImages(product.images?.map((img) => img.url || img) || []);

    form.setFieldsValue({
      name: product.name,
      category: product.categoryId,
      hsnCodeId: product.hsnCodeId,
      price: product.price,
      offerPrice: product.offerPrice,
      keywords: product.keywords,
      weightGrams: product.weightGrams,
    });

    setSpecs(
      product.specifications && product.specifications.length > 0
        ? product.specifications.map((s) => ({ label: s.label || "", value: s.value || "" }))
        : [{ label: "", value: "" }],
    );

    if (product.variants && product.variants.length > 0) {
      setHasVariants(true);
      setVariants(
        product.variants.map((v) => ({
          id: v.id,
          size: v.size || "",
          price: v.price || "",
          offerPrice: v.offerPrice || "",
          color: v.color || "",
          sku: v.sku || "",
          material: v.material || "",
        })),
      );
    } else {
      setHasVariants(false);
      setVariants([{ size: "", price: "", offerPrice: "", color: "", sku: "", material: "" }]);
    }

    setOpen(true);
  };

  // ================= CREATE PRODUCT =================
  const handleAddProduct = async () => {
    try {
      const values = await form.validateFields();

      const payload = {
        name: values.name,
        category: { id: values.category },
        hsn: values.hsnCodeId ? { id: values.hsnCodeId } : null,
        keywords: values.keywords || null,
        price: hasVariants ? null : Number(values.price),
        offerPrice: hasVariants
          ? null
          : (values.offerPrice !== undefined && values.offerPrice !== null && values.offerPrice !== ""
              ? Number(values.offerPrice)
              : null),
        weightGrams: values.weightGrams !== undefined && values.weightGrams !== null && values.weightGrams !== ""
          ? Number(values.weightGrams)
          : null,
        images: uploadedImages.map((url) => ({ url })),
        specifications: specs
          .filter((s) => s.label.trim())
          .map((s) => ({ label: s.label.trim(), value: s.value.trim() })),
        variants: hasVariants
          ? variants.map((v) => ({
              ...(v.id ? { id: v.id } : {}),
              size: v.size,
              price: Number(v.price),
              offerPrice: v.offerPrice !== "" && v.offerPrice != null ? Number(v.offerPrice) : null,
              color: v.color,
              material: v.material,
              sku: v.sku,
            }))
          : [],
      };

      if (mode === "add") {
        await createProductApi(payload);
      } else {
        await updateProductApi(editingProduct.id, payload);
      }

      setOpen(false);
      form.resetFields();
      setHasVariants(false);
      setVariants([{ size: "", price: "", offerPrice: "", color: "", sku: "", material: "" }]);
      setSpecs([{ label: "", value: "" }]);
      setUploadedImages([]);
      fetchProducts();
      message.success(mode === "add" ? "Product created!" : "Product updated!");
    } catch (err) {
      if (err?.errorFields) return; // Ant Design validation error — shown on fields automatically
      console.error("Save product error:", err);
      message.error(err?.response?.data?.data || "Failed to save product");
    }
  };

  // ================= FILTER =================
  // Search is server-side (see fetchProducts). Category filtering stays
  // client-side and only applies within the currently loaded page.
  const filtered = products.filter(
    (p) => cat === "All" || p.categoryName === cat,
  );

  // ================= TABLE =================
  const columns = [
    {
      title: "Product",
      dataIndex: "name",
      render: (v, r) => (
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {r.images?.[0] ? (
            <img
              src={r.images[0]}
              alt={v}
              style={{ width: 44, height: 44, objectFit: "cover", borderRadius: 8, border: "1px solid #eee", flexShrink: 0 }}
            />
          ) : (
            <div style={{ width: 44, height: 44, borderRadius: 8, background: "#f5f5f5", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, flexShrink: 0 }}>
              📦
            </div>
          )}
          <div>
            <div style={{ fontSize: 13, fontWeight: 500 }}>{v}</div>
            <div style={{ fontSize: 11, color: "#888" }}>ID: {r.id}</div>
          </div>
        </div>
      ),
    },

    {
      title: "Category",
      dataIndex: "categoryName",
      render: (v) => <span className="badge-pill badge-blue">{v || "-"}</span>,
    },

    {
      title: "HSN / GST",
      dataIndex: "hsnCode",
      render: (v, r) =>
        v ? (
          <Tooltip title={`HSN ${v}`}>
            <Tag color="purple">{r.gstRate}%</Tag>
          </Tooltip>
        ) : (
          <span style={{ color: "#ccc" }}>Not set</span>
        ),
    },

    {
      title: "Price",
      dataIndex: "priceLabel",
      render: (v, r) => (
        <span>
          {r.offerPrice != null ? (
            <>
              <span style={{ textDecoration: "line-through", color: "#999", marginRight: 6 }}>
                ₹{r.price}
              </span>
              <span style={{ fontWeight: 600, color: "#2e7d32" }}>₹{r.offerPrice}</span>
            </>
          ) : (
            <span style={{ fontWeight: 500, color: "#534ab7" }}>{v || "Not set"}</span>
          )}
        </span>
      ),
    },

{
  title: "Variants",
  dataIndex: "variants",
  render: (variants) => (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
      {variants?.slice(0, 3)?.map((v, i) => (
        <span key={i} className="badge-pill badge-blue">
          {v.size} / {v.color} -{" "}
          {v.offerPrice != null ? (
            <>
              <span style={{ textDecoration: "line-through", opacity: 0.6 }}>₹{v.price}</span>{" "}
              ₹{v.offerPrice}
            </>
          ) : (
            <>₹{v.price}</>
          )}
        </span>
      ))}
    </div>
  )
},


   {
  title: "Stock",
  dataIndex: "stock",
  render: (stock) => (
    <span
      style={{
        fontWeight: 500,
        color: stock > 0 ? "#2e7d32" : "#d32f2f"
      }}
    >
      {stock || 0}
    </span>
  )
}
,
    {
      title: "Status",
      dataIndex: "active",
      render: (active) => <Tag color={active ? "success" : "default"}>{active ? "Active" : "Inactive"}</Tag>,
    },

    {
      title: "Actions",
      width: 70,
      align: "center",
      render: (_, r) => (
        <Dropdown
          trigger={["click"]}
          menu={{
            items: [
              { key: "edit", label: "Edit", icon: <EditOutlined />, onClick: () => handleEdit(r) },
              {
                key: "toggle",
                label: r.active ? "Deactivate" : "Activate",
                icon: <PoweroffOutlined />,
                onClick: () => handleToggleActive(r),
              },
              { type: "divider" },
              { key: "delete", label: "Delete", icon: <DeleteOutlined />, danger: true, onClick: () => handleDelete(r.id) },
            ],
          }}
        >
          <Button icon={<MoreOutlined />} size="small" />
        </Dropdown>
      ),
    },
  ];

  return (
    <div>
      {/* HEADER */}
      <div className="page-header">
        <div>
          <div className="page-title">Products</div>
          <div className="page-sub">{pagination.total} items in catalogue</div>
        </div>

        <Button
          type="primary"
          onClick={() => {
            setMode("add");
            setEditingProduct(null);
            form.resetFields();
            setHasVariants(false);
            setVariants([{ size: "", price: "", offerPrice: "", color: "", sku: "", material: "" }]);
            setSpecs([{ label: "", value: "" }]);
            setUploadedImages([]);
            setOpen(true);
          }}
          style={{
            background: "#534ab7",
            border: "none",
            borderRadius: 8,
          }}
        >
          + Add Product
        </Button>
      </div>

      {/* FILTERS */}
      <div
        style={{
          display: "flex",
          gap: 8,
          marginBottom: 16,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        {[
          "All",
          ...new Set(products.map((p) => p.categoryName).filter(Boolean)),
        ].map((c) => (
          <button
            key={c}
            className={`filter-btn${cat === c ? " active" : ""}`}
            onClick={() => setCat(c)}
          >
            {c}
          </button>
        ))}

        <Search
          placeholder="Search products…"
          onSearch={handleSearch}
          allowClear
          style={{ width: 220, marginLeft: "auto" }}
        />
      </div>

      {/* TABLE */}
      <AppTable
        columns={columns}
        data={filtered}
        loading={loading}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          onChange: (page, pageSize) => setPagination((p) => ({ ...p, current: page, pageSize })),
        }}
      />

      {/* ================= MODAL ================= */}
      <Modal
        title={mode === "add" ? "Add Product" : "Edit Product"}
        open={open}
        onCancel={() => setOpen(false)}
        onOk={handleAddProduct}
        okText={mode === "add" ? "Save" : "Update"}
      >
        <Form form={form} layout="vertical">
          <AppInput
            name="name"
            label="Product Name"
            rules={[{ required: true }]}
            placeholder="Enter product name"
          />

          <div style={{ display: "flex", alignItems: "flex-end", gap: 8 }}>
            <div style={{ flex: 1 }}>
              <AppSelect
                name="category"
                label="Category"
                rules={[{ required: true }]}
                placeholder="Select category"
                options={categories.map((c) => ({
                  label: c.name,
                  value: c.id,
                }))}
              />
            </div>
            <Tooltip title="Manage categories">
              <Button
                icon={<PlusOutlined />}
                style={{ marginBottom: 24 }}
                onClick={() => navigate("/categories")}
              />
            </Tooltip>
          </div>

          <div style={{ display: "flex", alignItems: "flex-end", gap: 8 }}>
            <div style={{ flex: 1 }}>
              <AppSelect
                name="hsnCodeId"
                label="HSN Code (determines GST rate)"
                rules={[{ required: true, message: "Select an HSN code" }]}
                placeholder="Search HSN code…"
                showSearch
                filterOption={(input, option) =>
                  (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                }
                options={hsnCodes.map((h) => ({
                  label: `${h.code} — ${h.gstRate}%${h.description ? " · " + h.description : ""}`,
                  value: h.id,
                }))}
              />
            </div>
            <Tooltip title="Manage HSN / GST rates">
              <Button
                icon={<PlusOutlined />}
                style={{ marginBottom: 24 }}
                onClick={() => navigate("/hsn-codes")}
              />
            </Tooltip>
          </div>

          <AppInput
            name="keywords"
            label="Search Keywords (optional)"
            placeholder="e.g. baby lotion, moisturizer, skin care"
          />

          <AppInput
            name="weightGrams"
            label="Weight in grams (optional)"
            type="number"
            placeholder="e.g. 250 — used to quote real delivery charges at checkout"
          />

          <AppCheckbox
            checked={hasVariants}
            onChange={(e) => {
              const checked = e.target.checked;
              setHasVariants(checked);

              if (!checked) {
                setVariants([
                  { size: "", price: "", offerPrice: "", color: "", sku: "", material: "" },
                ]);
              }
            }}
            label="This product has variants"
          />

          {!hasVariants && (
            <div style={{ display: "flex", gap: 8 }}>
              <div style={{ flex: 1 }}>
                <AppInput
                  name="price"
                  label="Price"
                  type="number"
                  rules={[{ required: true, message: "Please enter price" }]}
                  placeholder="Enter price"
                />
              </div>
              <div style={{ flex: 1 }}>
                <AppInput
                  name="offerPrice"
                  label="Offer Price (optional)"
                  type="number"
                  placeholder="Discounted price"
                />
              </div>
            </div>
          )}

          {hasVariants && (
            <Form.Item label="Variants">
              {variants.map((v, index) => (
                <div
                  key={index}
                  style={{
                    border: "1px solid #eee",
                    padding: 10,
                    borderRadius: 8,
                    marginBottom: 10,
                  }}
                >
                  <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                    <AppInput
                      placeholder="Size"
                      value={v.size}
                      onChange={(e) => {
                        const newV = [...variants];
                        newV[index].size = e.target.value;
                        setVariants(newV);
                      }}
                    />
                    <AppInput
                      placeholder="Color"
                      value={v.color}
                      onChange={(e) => {
                        const newV = [...variants];
                        newV[index].color = e.target.value;
                        setVariants(newV);
                      }}
                    />
                  </div>

                  <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                    <AppInput
                      useFormItem={false}
                      placeholder="Material"
                      value={v.material}
                      onChange={(e) => {
                        const newV = [...variants];
                        newV[index].material = e.target.value;
                        setVariants(newV);
                      }}
                    />

                    <AppInput
                      useFormItem={false}
                      placeholder="SKU"
                      value={v.sku}
                      onChange={(e) => {
                        const newV = [...variants];
                        newV[index].sku = e.target.value;
                        setVariants(newV);
                      }}
                    />
                  </div>

                  <div style={{ display: "flex", gap: 8 }}>
                    <AppInput
                      useFormItem={false}
                      placeholder="Price"
                      type="number"
                      value={v.price}
                      onChange={(e) => {
                        const newV = [...variants];
                        newV[index].price = e.target.value;
                        setVariants(newV);
                      }}
                    />

                    <AppInput
                      useFormItem={false}
                      placeholder="Offer price (optional)"
                      type="number"
                      value={v.offerPrice}
                      onChange={(e) => {
                        const newV = [...variants];
                        newV[index].offerPrice = e.target.value;
                        setVariants(newV);
                      }}
                    />

                    <Button
                      danger
                      onClick={() =>
                        setVariants(variants.filter((_, i) => i !== index))
                      }
                    >
                      X
                    </Button>
                  </div>
                </div>
              ))}

              <Button
                type="dashed"
                onClick={() =>
                  setVariants([
                    ...variants,
                    { size: "", price: "", offerPrice: "", color: "", sku: "", material: "" },
                  ])
                }
              >
                + Add Variant
              </Button>
            </Form.Item>
          )}

          <Form.Item label="Specifications (optional)">
            {specs.map((s, index) => (
              <div key={index} style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                <AppInput
                  useFormItem={false}
                  placeholder="Label (e.g. Material)"
                  value={s.label}
                  onChange={(e) => {
                    const next = [...specs];
                    next[index].label = e.target.value;
                    setSpecs(next);
                  }}
                />
                <AppInput
                  useFormItem={false}
                  placeholder="Value (e.g. 100% Cotton)"
                  value={s.value}
                  onChange={(e) => {
                    const next = [...specs];
                    next[index].value = e.target.value;
                    setSpecs(next);
                  }}
                />
                <Button danger onClick={() => setSpecs(specs.filter((_, i) => i !== index))}>
                  X
                </Button>
              </div>
            ))}
            <Button type="dashed" onClick={() => setSpecs([...specs, { label: "", value: "" }])}>
              + Add Specification
            </Button>
          </Form.Item>

          <Form.Item label="Product Images">
            {/* Upload buttons row */}
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <Upload
                accept="image/*"
                beforeUpload={handleImageUpload}
                showUploadList={false}
                disabled={uploadingIdx !== null || aiLoading}
              >
                <Button icon={<UploadOutlined />} loading={uploadingIdx !== null && !aiLoading}>
                  Upload Image
                </Button>
              </Upload>

              <Tooltip title="Uploads image + asks Gemini AI to generate 3 e-commerce photography prompts">
                <Upload
                  accept="image/*"
                  beforeUpload={handleAiUpload}
                  showUploadList={false}
                  disabled={uploadingIdx !== null || aiLoading}
                >
                  <Button
                    icon={<RobotOutlined />}
                    loading={aiLoading}
                    style={{ background: "#f0eeff", borderColor: "#534ab7", color: "#534ab7" }}
                  >
                    Upload with AI ✨
                  </Button>
                </Upload>
              </Tooltip>
            </div>

            {/* Image thumbnails */}
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 8 }}>
              {uploadedImages.map((url, i) => (
                <div key={i} style={{ position: "relative" }}>
                  <img
                    src={url}
                    alt=""
                    style={{ width: 80, height: 80, objectFit: "cover", borderRadius: 8, border: "1px solid #eee" }}
                  />
                  <Button
                    danger
                    size="small"
                    icon={<DeleteOutlined />}
                    style={{ position: "absolute", top: 2, right: 2, padding: "0 4px", minWidth: 0 }}
                    onClick={() => handleRemoveImage(url)}
                  />
                </div>
              ))}
            </div>

            {/* AI loading */}
            {aiLoading && (
              <div style={{ marginTop: 12, padding: "12px 16px", background: "#f9f8ff", borderRadius: 8, border: "1px solid #e0dcff", display: "flex", alignItems: "center", gap: 10 }}>
                <Spin size="small" />
                <span style={{ color: "#534ab7", fontSize: 13 }}>Gemini is generating 3 product images… this may take up to a minute</span>
              </div>
            )}
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
